<template>
  <div v-if="shouldShowWidget" class="card h-100 results-messages-widget">
    <div v-if="debug" class="container mt-5">
      Prompt:
      <textarea v-model="canMessage" class="form-control mb-3" rows="15" />
      <button
        class="btn btn-secondary btn-sm mb-3"
        @click="sendMessageDebug"
        :disabled="loadingRealtime"
      >
        Send
      </button>
    </div>
    <div class="card-body">
      <div
        v-if="error"
        class="widget-error d-flex align-items-center justify-content-center"
      >
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        <span>{{ error }}</span>
      </div>

      <div v-else-if="maxWaitingPassed" class="widget-timeout">
        <p>
          {{ $t('widgetResultsMessages.error.generic') }}
        </p>
        <button class="btn btn-primary btn-sm mt-3" @click="retry">
          {{ $t('widgetResultsMessages.retry') }}
        </button>
      </div>

      <div v-else-if="messages.length > 0" class="widget-content">
        <div class="row">
          <div
            v-for="text in columnsForMessages"
            class="col col-12 col-sm-6"
            :key="text"
          >
            <div class="messages-text" v-html="markdownToHtml(text)"></div>
          </div>
        </div>

        <!-- Follow-up Proposals -->
        <div v-if="proposalsFromAI.length" class="proposals-section mt-4">
          <hr />
          <div class="proposals-header mt-8 mb-2"></div>
          <div class="proposals-buttons d-flex justify-content-center gap-4">
            <button
              v-for="proposal in proposalsFromAI"
              :key="proposal"
              class="btn btn-primary btn-sm text-start"
              style="max-width: 300px"
              @click="handleProposalClick(proposal)"
            >
              {{ proposal }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-else
        class="widget-waiting d-flex align-items-center justify-content-center"
      >
        <div
          v-if="loadingRealtime"
          class="d-flex flex-column align-items-center"
        >
          <div class="spinner-border" role="status"></div>
          <span class="ms-2 mt-2">{{
            $t('widgetResultsMessages.loading')
          }}</span>
        </div>
        <div v-else class="d-flex flex-column align-items-center">
          <div class="spinner-grow mt-2" role="status"></div>
          <span class="ms-2 mt-2">{{
            $t('widgetResultsMessages.waiting')
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import commonWidgetProps from '@/components/mixins/common-widget-props'
import { requestTypes } from '@/constants/request-types'
import MarkdownIt from 'markdown-it'
import { useRealtime } from '@/components/realtime/realtime'

export const resultsMessagesWidgetInformation = ({
  colData,
  userIsParticipatingInSurvey,
  userCanSelectAlertActions,
  userHasConfirmedActions
}) => {
  const requests = {
    [requestTypes.SURVEY_DETAILS]: {},
    [requestTypes.TAGS]: {},
    [requestTypes.CATEGORIES]: {}
  }
  return { requests }
}

export const resultsMessagesWidgetSkeleton = () => {
  return {
    body: [
      {
        cols: [{ height: 2 }, { height: 2 }]
      },
      {
        cols: [{ height: 2 }, { height: 2 }]
      },
      {
        cols: [{ height: 2 }, { height: 2 }]
      },
      {
        cols: [{ height: 2 }, { height: 2 }]
      }
    ]
  }
}

const ResultsMessagesWidget = {
  name: 'ResultsMessagesWidget',
  mixins: [commonWidgetProps],
  props: {
    categories: {
      type: Object,
      default: () => {}
    },
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    filteredSurveyDetails: {
      type: Object,
      default: () => {}
    },
    tags: {
      type: Object,
      default: () => {}
    },
    questions: {
      type: Object,
      default: () => {}
    },
    trendCategories: {
      type: Object,
      default: () => {}
    },
    trendTags: {
      type: Object,
      default: () => {}
    },
    trendQuestions: {
      type: Object,
      default: () => {}
    },
    actions: {
      type: Array,
      default: () => []
    },
    shouldShowResultsMessages: {
      type: Boolean,
      default: false
    },
    selectedGroup: {
      type: Object,
      default: () => {}
    },
    selectedAttributeSegment: {
      type: Object,
      default: () => {}
    },
    surveyName: {
      type: String,
      default: ''
    },
    organizationName: {
      type: String,
      default: ''
    },
    organizationFullName: {
      type: String,
      default: ''
    },
    section: {
      type: String,
      default: 'overview'
    }
  },
  setup(props) {
    const realtime = useRealtime(props)
    return {
      realtime
    }
  },
  data() {
    return {
      canMessage2: `Provide brief overview of results in two sections: key findings and recommendations. IMPORTANT: always connect impact on a finding and recommendation to a business goal. Response should be in this exact format (but translate headers to users locale if not 'en'):


### Overall

* [finding 1]
* [finding 2]
* [finding 3]

---

### Recommendations

* [recommendation 1]
* [recommendation 2]
* [recommendation 3]

IMPORTANT: For each recommendation, always add the most important discussion point for the team to work with that recommendation

No other headlines except these two are allowed.

You should focus on how to improve engagement and wellbeing in order to drive better business outcomes. Use emojis where applicable. The response should be in this language: ${this.$i18n.locale}`,
      waitingTimer: null,
      secondsWaiting: 0,
      maxSecondsWaiting: 15,
      maxWaitingPassed: false,
      resultsTag: 'resultssummary',
      resultsSessionId: 'resultssummary',
      sessionMessages: {} // Store messages for the dedicated session
    }
  },
  computed: {
    debug() {
      return this.$route.query.debugprompt === 'true'
    },
    shouldShowWidget() {
      return (
        this.shouldShowResultsMessages &&
        this.hasValidResults &&
        this.surveyDetails.details.status === 'completed' &&
        this.$route.query.airesults === 'true'
      )
    },
    columnsForMessages() {
      if (this.messages.length) {
        // look for markdown formatted horizontal line and split by that
        return this.messages[0].text.split('---').map((col) => col.trim())
      }
    },

    messages() {
      // If we have sessionMessages (cached from localStorage), use it

      const modelsFoundInSessionMessages = Object.keys(this.sessionMessages)
      const actualSessionMessages = Object.keys(this.sessionMessages).length
        ? this.sessionMessages[modelsFoundInSessionMessages[0]]
        : []

      const actualSessionMessagesFound =
        actualSessionMessages &&
        actualSessionMessages.length > 0 &&
        actualSessionMessages.some((m) => m.direction === 'in')

      if (actualSessionMessagesFound) {
        const messagesFound =
          this.sessionMessages[
            modelsFoundInSessionMessages[
              modelsFoundInSessionMessages.length - 1
            ]
          ]

        if (!messagesFound || !messagesFound.length) {
          return []
        }

        return messagesFound
          .filter((m) => m.direction === 'in')
          .map((m) => {
            const { text, data } = this.realtime.extractDataFromMessage(m)
            return {
              ...m,
              text,
              data
            }
          })
      }

      // Otherwise, read from realtime.messages (live WebSocket updates)
      // This is automatically reactive because we reference realtime.messages
      const modelsFound = Object.keys(this.realtime.messages)
      if (!modelsFound.length) {
        return []
      }

      const messagesFound =
        this.realtime.messages[modelsFound[modelsFound.length - 1]]
      if (!messagesFound || !messagesFound.length) {
        return []
      }

      return messagesFound
        .filter(
          (m) => m.direction === 'in' && m.order === 1 // Only first response message
        )
        .map((m) => {
          const { text, data } = this.realtime.extractDataFromMessage(m)
          return {
            ...m,
            text,
            data
          }
        })
    },
    loadingRealtime() {
      return this.realtime.loading
    },
    error() {
      return this.realtime.error
    },
    waitingForResponse() {
      return this.realtime.waitingForResponse
    },
    proposalsFromAI() {
      // Get proposals from the first filtered message that has data
      if (this.messages.length > 0) {
        for (const message of this.messages) {
          if (message.data && message.data.followUpProposals) {
            return message.data.followUpProposals
          }
        }
      }
      return []
    },
    dataInResponse() {
      // Get data from first message with data
      if (this.messages.length > 0) {
        for (const message of this.messages) {
          if (message.data) {
            return message.data
          }
        }
      }
      return {}
    },
    hasValidResults() {
      return Object.keys(this.categories || {}).some((categoryName) =>
        Object.keys(this.categories[categoryName]).includes('value')
      )
    }
  },
  watch: {
    // todo: check if this is needed
    shouldShowWidget(newVal) {
      if (newVal) {
        this.realtime.setSection({ section: this.section })
        this.loadOrSendMessage()
      }
    },
    // Watch store messages to trigger re-render for real-time updates
    // todo: check if this is needed
    'realtime.messages': {
      handler() {
        // This forces the computed property to recompute
        this.$forceUpdate()
      },
      deep: true
    },
    error(e) {
      if (e.message === 'ExpiredTokenException') {
        // Token expired, reload page to get a new one
        console.log('Token expired. Reloading page.')
        window.location.reload()
      }
    }
  },
  mounted() {
    if (this.shouldShowWidget) {
      this.realtime.setSection({ section: this.section })
      // this.realtime.clearAllStoredMessages() // just for testing

      if (this.debug) {
        this.realtime.clearAllStoredMessages()
      } else {
        this.loadOrSendMessage()
      }
    }
  },
  beforeUnmount() {
    if (this.waitingTimer) {
      clearInterval(this.waitingTimer)
    }
  },
  methods: {
    markdownToHtml(text) {
      const md = new MarkdownIt()
      return md.render(text)
    },
    loadOrSendMessage() {
      // Load messages from the dedicated resultssummary session
      // without changing the active session

      const storedMessages = this.realtime.getMessagesFromSpecificSession(
        this.resultsSessionId
      )

      if (storedMessages && Object.keys(storedMessages).length > 0) {
        // Load messages from the dedicated session
        this.sessionMessages = storedMessages
      } else {
        // No stored result found, send new message
        this.sessionMessages = {}
        this.sendMessage()
      }
    },
    sendMessageDebug() {
      this.realtime.clear()
      this.realtime.clearAllStoredMessages()
      this.sessionMessages = {}
      this.sendMessage()
    },
    sendMessage() {
      const date = new Date()
      const unixEpochTime = Math.floor(date.getTime() / 1000)
      const userMessage = {
        direction: 'out',
        text: this.canMessage,
        timestamp: date,
        unixEpochTime,
        order: 0,
        modelName: 'bedrock',
        tags: ['resultssummary']
      }

      this.realtime.sendRealtimeMessageActionWithSessionId({
        sessionId: this.resultsSessionId,
        message: userMessage,
        surveyName: this.surveyName,
        groupName: this.selectedGroup.name,
        aggregate: this.realtime.surveyIsAggregate,
        surveyData: this.realtime.surveyData
      })

      // Start waiting timer
      this.waitingTimer = setInterval(() => {
        if (this.waitingForResponse) {
          this.secondsWaiting = this.realtime.secondsWaitingForResponse()
          if (this.secondsWaiting > this.maxSecondsWaiting) {
            this.maxWaitingPassed = true
            this.secondsWaiting = 0
            clearInterval(this.waitingTimer)
          }
        } else {
          this.secondsWaiting = 0
          clearInterval(this.waitingTimer)
          // Give it a moment for localStorage to be updated
          setTimeout(() => {
            this.loadFromDedicatedSession()
          }, 500)
        }
      }, 1000)
    },
    loadFromDedicatedSession() {
      // After response arrives, reload from dedicated session
      const storedMessages = this.realtime.getMessagesFromSpecificSession(
        this.resultsSessionId
      )
      this.sessionMessages = storedMessages
    },
    retry() {
      this.maxWaitingPassed = false
      this.secondsWaiting = 0
      // Load existing messages from dedicated session
      this.loadOrSendMessage()
    },
    handleProposalClick(proposal) {
      // Emit event for parent component to handle with sessionId
      this.$emit('proposal-selected', {
        proposal,
        sessionId: this.resultsSessionId
      })
    }
  }
}

export default ResultsMessagesWidget
</script>

<style lang="scss" scoped>
.results-messages-widget {
  .card-body {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 150px;
  }

  .widget-loading,
  .widget-waiting,
  .widget-timeout {
    color: var(--kt-text-muted);
  }

  .widget-error {
    color: var(--kt-danger);
  }

  .message-item {
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--kt-border);

    &:last-child {
      border-bottom: none;
    }
  }
}
</style>

<style>
.results-messages-widget .messages-text ul li {
  font-size: 1.25rem;

  margin-bottom: 1.5rem;
}

.results-messages-widget .messages-text h1 {
  font-size: 1.9rem;
  margin-bottom: 1.5rem;
}
.results-messages-widget .messages-text h2 {
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
}
.results-messages-widget .messages-text h3 {
  font-size: 1.7rem;
  margin-bottom: 1.5rem;
}
</style>
